import { defaultRtn, RtnType } from "../../utilsTs/declareTypes";

/** 对象处理 */
const objectHandle = async () => { };

/** callbackEntry */
interface CallbackEntry<T> {
    /** 回调 */
    cb: T;
    /** Timeout id */
    timeoutId?: NodeJS.Timeout;
}

/** 回调管理器 */
objectHandle.callbackManager = <T extends (...args: any[]) => void>() => {
    /** 使用Map存储，提高键类型灵活性 */
    const callbackMap = new Map<string, CallbackEntry<T>>();
    /**
     * 执行回调并清除定时器
     * @param id 
     */
    const clearTimeoutExe = (
        /** id */
        id: string,
        /** 数据 */
        data?: any) => {
        let res = false;
        const entry = callbackMap.get(id);
        if (entry) {
            entry.cb(data); // 执行回调
            // 清除定时器并删除条目
            if (entry.timeoutId) {
                clearTimeout(entry.timeoutId);
            }
            callbackMap.delete(id);
            res = true;
        }
        return res;
    }
    return {
        /** 增加 */
        add: (
            /** id */
            id: string,
            /** 回传 */
            cb: T,
            /** 选项 */
            options?: {
                /** 覆盖，默认：true */
                isCover?: boolean,
                /** 到期时间(秒) */
                expires?: number
            }
        ): boolean => {
            const { isCover, expires } = options || {};
            let res = true;
            if (isCover === false && callbackMap.has(id)) {
                res = false;
            }
            if (res === true) {
                /** 清除旧的定时器（如果存在） */
                const existing = callbackMap.get(id);
                if (existing?.timeoutId) {
                    clearTimeout(existing.timeoutId);
                }
                /** 存储新回调及定时器ID */
                const newEntry: CallbackEntry<T> = { cb };
                if (expires) {
                    newEntry.timeoutId = setTimeout(() => {
                        callbackMap.delete(id);
                    }, expires * 1000);
                }
                callbackMap.set(id, newEntry);
            }
            return res;
        },
        /** 获取 */
        get: (
            /** id */
            id: string): T | undefined => { return callbackMap.get(id)?.cb },
        /** 返回所有 */
        getAll: (): any => { return callbackMap },
        /** 删除 */
        remove: (
            /** id */
            id: string) => callbackMap.delete(id),
        /** 清空 */
        clear: () => callbackMap.clear(),
        /** ：触发所有回调 */
        triggerAll: (
            /** 数据 */
            data?: any) => {
            callbackMap.forEach(entry => entry.cb(data));
        },
        /** 执行一次 */
        disposable: (
            /** id */
            id: string,
            /** 数据 */
            data?: any
        ): boolean => {
            return clearTimeoutExe(id, data);
        }
    };
};

/**
 * 如果对象内容是：undefined、null、''，则判断为空值
 * @param obj 对象
 * @param force 如果对象内容是字符串：'undefined' 、 'null'，也判断为空值
 * @returns 如果空值，返回true
 */
objectHandle.isEmpty = (obj: any, force?: boolean) => {
    let res = false;
    if (typeof obj === 'undefined' || obj === null || obj === '') {
        res = true;
    }
    if (force === true) {
        if (obj === 'undefined' || obj === 'null') {
            res = true;
        }
    }
    return res;
};

/**
 * 是否对象
 * @param value 变量
 * @returns 
 */
objectHandle.isObject = (value: unknown): value is Record<string, unknown> => {
    return value !== null && typeof value === 'object' && !Array.isArray(value) && Object.prototype.toString.call(value) === '[object Object]';
};

/**
 * 是否数字
 * @param value 
 * @returns 
 */
objectHandle.isNumber = (value: unknown): value is number => {
    return typeof value === 'number' && !isNaN(value);
}


/**
 * 字符串'false/true'转boolean
 * @param str 字符串
 * @returns 
 */
objectHandle.parseBoolean = (str: unknown): boolean => {
    // 先进行类型过滤
    if (typeof str !== 'string') {
        if (typeof str === 'boolean') return str; // 直接返回布尔值
        throw new Error(`Invalid type: ${typeof str}`);
    }
    // 统一转为小写并严格匹配
    const lowerStr = str.trim().toLowerCase();
    return lowerStr === 'true'; // 仅接受'true'返回true
};

/** 环境类型 */
export declare type EnvironmentType = 'production' | 'development' | 'localDev';

/** 配置选项 */
export interface ConfigurationOptions {
    /** 默认变量 */
    defaultValue?: string | number | boolean,
    /** 配置名称前缀 */
    configNamePrefix?: string,
    /** 配置名称后缀 */
    configNameSuffix?: string
};

/**
 * 获取配置
 * @param environment 环境变量
 * @param configName 配置名称
 * @param options 选项
 */
objectHandle.getConfiguration = (
    environment: EnvironmentType,
    configName: string,
    options?: ConfigurationOptions) => {
    let rtn: any;
    const { defaultValue, configNamePrefix, configNameSuffix } = options || {};
    /**
     * 添加前缀后缀
     * @param value 
     * @returns 
     */
    const addPrefixAndSuffix = (value: string) => {
        if (typeof configNamePrefix !== 'undefined') {
            value = `${configNamePrefix}${value}`;
        }
        if (typeof configNameSuffix !== 'undefined') {
            value = `${value}${configNameSuffix}`;
        }
        return value;
    }
    switch (environment) {
        //生产模式
        case 'production':
            configName = addPrefixAndSuffix(configName);
            break;
        //开发模式
        case 'development':
            configName = addPrefixAndSuffix(`DEV_${configName}`);
            break;
        //本地开发模式
        case 'localDev':
            configName = addPrefixAndSuffix(`LOCALDEV_${configName}`);
            break;
    }
    rtn = process.env[configName] || (typeof defaultValue !== 'undefined' ? defaultValue : '');
    switch (typeof defaultValue) {
        case 'boolean':
            rtn = objectHandle.parseBoolean(rtn);
            break;
        case 'number':
            rtn = Number(rtn);
            break;
    }
    return rtn;
};

/**
 * 深度比较
 * @param obj1
 * @param obj2 
 * @returns 
 */
objectHandle.deepCompare = (obj1: object, obj2: object): object => {
    const diff: Record<string, any> = {};
    // 遍历所有属性（含原型链检查）
    for (const key in { ...obj1, ...obj2 }) {
        const val1 = obj1[key as keyof typeof obj1];
        const val2 = obj2[key as keyof typeof obj2];
        if (typeof val1 === "object" && typeof val2 === "object") {
            const nestedDiff = objectHandle.deepCompare(val1, val2);
            if (Object.keys(nestedDiff).length > 0) {
                diff[key] = nestedDiff; // 记录嵌套差异
            }
        } else if (val1 !== val2) {
            diff[key] = val2; // 记录基础类型差异
        }
    }
    return diff;
};

/**
 * 深度比较两个对象的键名结构（包括嵌套对象）
 * @param obj1 任意对象1
 * @param obj2 任意对象2
 * @param parentPath 递归时传递的父级路径（内部使用）
 * @returns 包含比较结果和差异描述的对象
 */
objectHandle.compareKeysDeep = (
    obj1: unknown,
    obj2: unknown,
    parentPath: string = ""
): RtnType => {
    let rtn = defaultRtn({ success: true });
    try {
        // 类型检查：两者必须均为对象
        if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
            rtn.success = false;
            rtn.message = `${parentPath || "根层级"}：输入不是对象`;
        } else {
            const keys1 = Object.keys(obj1);
            const keys2 = Object.keys(obj2);
            const keySet1 = new Set(keys1);
            const keySet2 = new Set(keys2);
            const allKeys = new Set([...keys1, ...keys2]);
            // 检查键数量差异
            if (keys1.length !== keys2.length) {
                rtn.success = false;
                rtn.message = `${parentPath || "根层级"}：键数量不同（${keys1.length} vs ${keys2.length}）`;
            } else {
                const missingKeys: string[] = [];
                // 收集键名差异
                for (const key of allKeys) {
                    const fullPath = parentPath ? `${parentPath}.${key}` : key;
                    if (!keySet1.has(key)) {
                        missingKeys.push(`${fullPath}（在对象1中缺失）`);
                    }
                    if (!keySet2.has(key)) {
                        missingKeys.push(`${fullPath}（在对象2中缺失）`);
                    }
                }
                // 存在键名缺失时直接返回
                if (missingKeys.length > 0) {
                    rtn.success = false;
                    rtn.message = `键名差异：${missingKeys.join("，")}`;
                } else {
                    // 递归检查嵌套对象
                    for (const key of keys1) {
                        const val1 = (obj1 as Record<string, unknown>)[key];
                        const val2 = (obj2 as Record<string, unknown>)[key];
                        const fullPath = parentPath ? `${parentPath}.${key}` : key;
                        // 两者均为对象 => 递归比较
                        if (typeof val1 === "object" && val1 !== null && typeof val2 === "object" && val2 !== null) {
                            const nestedResult = objectHandle.compareKeysDeep(val1, val2, fullPath);
                            if (nestedResult.success !== true) {
                                rtn = nestedResult; // 返回嵌套层级的错误
                            }
                        }
                        // 类型不一致（一个对象一个非对象）
                        else if (
                            (typeof val1 === "object" && typeof val2 !== "object") ||
                            (typeof val1 !== "object" && typeof val2 === "object")
                        ) {
                            rtn.success = false;
                            rtn.message = `${fullPath}：类型不匹配（${typeof val1} vs ${typeof val2}）`;
                        }
                    }
                }
            }
        }
    }
    catch (error: any) {
        rtn.message = '错误！';
        if (error) {
            const { message } = error;
            rtn.message = `错误！${message}`;
        }
    }
    return rtn;
}

/**
 * 合并对象
 * @param def 默认对象(合并时会被修改)
 * @param obj 所要合并对象
 * @param numberAdd 是否需要数值相加，默认false
 * @returns 返回合并后对象
 */
objectHandle.mergeObject = (def: { [x: string]: any; }, obj: { [x: string]: any; }, numberAdd?: boolean) => {
    // console.log('----------start------------');
    // console.log('def:');
    // console.log(def);
    // console.log('obj:');
    // console.log(obj);
    // console.log('--------end--------------');
    if (!obj) {
        return def;
    }
    else if (!def) {
        return obj;
    }
    for (var i in obj) {
        // if its an object
        if (typeof obj[i] !== 'undefined' && obj[i] !== null && obj[i].constructor === Object) {
            def[i] = objectHandle.mergeObject(def[i], obj[i]);
        }
        // if its an array, simple values need to be joined.  Object values need to be re-merged.
        else if (typeof obj[i] !== 'undefined' && obj[i] !== null && (obj[i] instanceof Array) && obj[i].length > 0) {
            // test to see if the first element is an object or not so we know the type of array we're dealing with.
            if (obj[i][0].constructor === Object) {
                var newobjs = [];
                // create an index of all the existing object IDs for quick access.  There is no way to know how many items will be in the arrays.
                var objids = {} as any;
                for (var x = 0, l = def[i].length; x < l; x++) {
                    objids[def[i][x].id] = x;
                }
                // now walk through the objects in the new array
                // if the ID exists, then merge the objects.
                // if the ID does not exist, push to the end of the def array
                for (let x = 0, l = obj[i].length; x < l; x++) {
                    var newobj = obj[i][x];
                    if (typeof objids[newobj.id] !== "undefined" && objids[newobj.id] !== null) {
                        def[i][x] = objectHandle.mergeObject(def[i][x], newobj);
                    }
                    else {
                        newobjs.push(newobj);
                    }
                }
                for (let x = 0, l = newobjs.length; x < l; x++) {
                    def[i].push(newobjs[x]);
                }
            }
            else {
                for (let x = 0; x < obj[i].length; x++) {
                    let idxObj = obj[i][x];
                    if (def[i].indexOf(idxObj) === -1) {
                        def[i].push(idxObj);
                    }
                }
            }
        }
        else {
            if (isNaN(obj[i]) || i.indexOf('_key') > -1) {
                def[i] = obj[i];
            }
            else {
                //不需要数值相加
                if (numberAdd === true) {
                    def[i] += obj[i];
                }
                else {
                    def[i] = obj[i];
                }
            }
        }
    }
    // console.log('finish: ');
    // console.log(def);
    // console.log('------------finish-----------');
    return def;
};

export default objectHandle;